/* eslint-disable */
// Extractor de datos de vehículo a partir de la respuesta cruda de Murphy
// (post-Utility.join + post-MurphyService.createQuery).
//
// Por qué este módulo existe:
// El joiner de Murphy genera N filas en `result.queryObjects` agrupadas por
// hash de [placa, serie, vehiculo, estado] (glueProperties). Cada fila trae
// un `extendedData[]` con las propiedades NO-glue de cada fuente, etiquetadas
// por `srcName`. La implementación previa hacía:
//   var vehiculoData = result.queryObjects[0].properties
// con un Object.assign-ish destructivo que perdía info por:
//   1. Solo leer la PRIMERA fila (descartando placas asociadas al mismo VIN).
//   2. Aplanar todos los srcName en `properties` con la misma key, sobre-
//      escribiendo silenciosamente campos con semántica distinta:
//         - `fecha_robo` ESTATUS (ISO de cuándo se robó)
//           vs ALERTA (dd/m/yyyy HH:mm de cuándo se levantó el reporte)
//         - `domicilio` VEHICULOS_EDOMEX (del propietario)
//           vs ALERTA (dirección donde se cometió el robo)
//         - `averiguacion` ESTATUS (folio corto)
//           vs ALERTA (formato fiscalía con prefijo de unidad)
//         - `reporte` con 4 valores semánticamente distintos según fuente
//   3. Tomar como verdadero cualquier valor "undefined" / "SIN INFORMACION"
//      cuando otra fuente sí tenía el dato real.
//
// Este extractor recorre las extendedData[] todas las filas, prioriza por
// fuente confiable según el campo, y emite tres objetos separados:
//   - vehiculoData : datos del vehículo + propietario
//   - roboData     : todo lo relativo a un eventual reporte de robo
//   - placasAsociadas : array de placas distintas con el mismo VIN
//
// Las fuentes vistas en producción (PLACA query):
//   OCRA PLACA, PADRON VEHICULAR SS EDOMEX, ESTATUS VEHICULAR EDOMEX,
//   CARFAX PLACA, VEHICULOS ROBADOS NAYARIT, VEHICULOS NAYARIT,
//   VEHICULOS EDOMEX, ALERTA VEHICULAR, ALPR PLACA.

var SRC = {
  ESTATUS: 'ESTATUS VEHICULAR EDOMEX',
  ALERTA: 'ALERTA VEHICULAR',
  EDOMEX: 'VEHICULOS EDOMEX',
  NAYARIT: 'VEHICULOS NAYARIT (PLACA)',
  ROBADOS_NAY: 'VEHICULOS ROBADOS NAYARIT (PLACA)',
  ALPR: 'ALPR PLACA',
}

// Strings que el backend devuelve como placeholder de "no hay dato". Si
// otra fuente tiene un valor real, lo preferimos por encima de estos.
var GARBAGE_VALUES = {
  '': true,
  'undefined': true,
  'null': true,
  'SIN INFORMACION': true,
  'SIN INFORMACIÓN': true,
  'NO DISPONIBLE': true,
  'N/A': true,
  '—': true,
}

function isGarbage(v) {
  if (v == null) return true
  var s = String(v).trim().toUpperCase()
  return !!GARBAGE_VALUES[s] || !!GARBAGE_VALUES[String(v).trim()]
}

// Toma el primer valor no-basura de una lista de candidatos.
function pickFirst(values) {
  for (var i = 0; i < values.length; i++) {
    if (!isGarbage(values[i])) return values[i]
  }
  return null
}

// Lee un campo de un mapa srcName→{name:value}, pasando por sanitización.
function getFromSrc(bySrc, srcName, field) {
  var bag = bySrc[srcName]
  if (!bag) return null
  var v = bag[field]
  return isGarbage(v) ? null : v
}

// Indexa baseProperties + extendedData de UNA fila post-join en
// `{ srcName: { fieldName: value }, base: { fieldName: value } }`.
// `base` es el subset de glueProperties (placa/serie/vehiculo/estado) que
// son comunes a todas las fuentes que produjeron esta fila.
function indexRow(row) {
  var bySrc = {}
  var base = {}
  if (Array.isArray(row.baseProperties)) {
    for (var i = 0; i < row.baseProperties.length; i++) {
      var bp = row.baseProperties[i]
      if (bp && bp.name) base[bp.name] = bp.value
    }
  }
  if (Array.isArray(row.extendedData)) {
    for (var j = 0; j < row.extendedData.length; j++) {
      var ed = row.extendedData[j]
      if (!ed || !ed.srcName || !Array.isArray(ed.properties)) continue
      var bag = bySrc[ed.srcName] || (bySrc[ed.srcName] = {})
      for (var k = 0; k < ed.properties.length; k++) {
        var p = ed.properties[k]
        if (p && p.name) bag[p.name] = p.value
      }
    }
  }
  return { bySrc: bySrc, base: base }
}

// El joiner de Murphy hashea por [placa, serie, vehiculo, estado]. Cuando
// una fuente regresa esos campos como placeholder (ALPR de placas robadas
// suele traer `serie="SIN INFORMACION"` y `vehiculo="UNDEFINED"`) cae en
// una fila distinta de ESTATUS/ALERTA aunque sea el mismo vehículo. Para
// distinguir filas REALES (otra placa con el mismo VIN) de filas FANTASMA
// (mismo vehículo, baseProperties basura), miramos si `serie` está sano.
// `vehiculo` no nos sirve como discriminante porque ESTATUS y EDOMEX a
// veces difieren ("CHEVROLET CHEVY 2006" vs "GENERAL MOTORS CHEVY 2006"
// generan filas distintas legítimas con MISMO VIN, MISMA placa).
function isPhantomRow(indexed) {
  var s = indexed.base && indexed.base.serie
  return isGarbage(s)
}

// Construye el objeto { vehiculoData, roboData, placasAsociadas } a partir
// del array `result.queryObjects` que devuelve MurphyService.createQuery.
//
// Regla general de prioridad: la fuente más específica/confiable al campo
// gana. Para campos comunes, se itera en orden ESTATUS → ALERTA → EDOMEX →
// ALPR (ALPR como ultimo recurso porque sus campos a veces son "undefined"
// literal). Para campos exclusivos de una fuente, se lee directo de ahí.
//
// `placasAsociadas` solo se llena cuando hay >1 fila con baseProperties
// distintas (caso recuperado.json: mismo VIN, 3 placas históricas).
export function extractVehiculoFromMurphyResult(queryObjects) {
  var rows = Array.isArray(queryObjects) ? queryObjects : []
  if (rows.length === 0) {
    return { vehiculoData: {}, roboData: null, placasAsociadas: [] }
  }

  var indexed = rows.map(indexRow)

  // La fila "principal" es la primera fila NO fantasma — una fila fantasma
  // es la que solo aporta hits ALPR con `serie="SIN INFORMACION"` (no son
  // un vehículo distinto, son la misma placa con baseProperties pobres).
  // Si todas son fantasmas (caso degenerado), caemos a indexed[0].
  var main = null
  for (var mi = 0; mi < indexed.length; mi++) {
    if (!isPhantomRow(indexed[mi])) { main = indexed[mi]; break }
  }
  if (!main) main = indexed[0]
  var base = main.base

  // Consolidamos `bySrc` desde TODAS las filas: las fuentes que comparten
  // el mismo VIN-real generan UNA fila (ESTATUS+ALERTA+EDOMEX se mergean
  // cuando matchean glueProperties), pero ALPR cae en otra fila por su
  // baseProperties pobre. Sin esta consolidación, el lprData del ALPR se
  // perdería. Para campos que aparecen en varias fuentes, la fila principal
  // pisa a las fantasma (los datos sanos ganan).
  var bySrc = {}
  for (var ri = 0; ri < indexed.length; ri++) {
    var rb = indexed[ri].bySrc
    for (var src in rb) {
      if (!Object.prototype.hasOwnProperty.call(rb, src)) continue
      if (!bySrc[src]) bySrc[src] = {}
      for (var fk in rb[src]) {
        if (!Object.prototype.hasOwnProperty.call(rb[src], fk)) continue
        // Para la fila principal, sobrescribir siempre. Para las demás,
        // solo si el campo aún no existía o era basura.
        if (indexed[ri] === main || isGarbage(bySrc[src][fk])) {
          bySrc[src][fk] = rb[src][fk]
        }
      }
    }
  }

  // ──────────────────────── vehiculoData ────────────────────────
  // Datos del vehículo (glue) — preferir EDOMEX (es el "padrón" oficial),
  // luego ESTATUS, luego ALERTA. ALPR queda al final porque a veces emite
  // "undefined" literal para vehiculo/serie.
  var vehiculoData = {
    placa: pickFirst([
      base.placa,
      getFromSrc(bySrc, SRC.EDOMEX, 'placa'),
      getFromSrc(bySrc, SRC.ESTATUS, 'placa'),
      getFromSrc(bySrc, SRC.ALERTA, 'placa'),
    ]),
    serie: pickFirst([
      base.serie,
      getFromSrc(bySrc, SRC.EDOMEX, 'serie'),
      getFromSrc(bySrc, SRC.ESTATUS, 'serie'),
      getFromSrc(bySrc, SRC.ALERTA, 'serie'),
    ]),
    vehiculo: pickFirst([
      base.vehiculo,
      getFromSrc(bySrc, SRC.EDOMEX, 'vehiculo'),
      getFromSrc(bySrc, SRC.ESTATUS, 'vehiculo'),
      getFromSrc(bySrc, SRC.ALERTA, 'vehiculo'),
    ]),
    estado: pickFirst([
      base.estado,
      getFromSrc(bySrc, SRC.EDOMEX, 'estado'),
      getFromSrc(bySrc, SRC.ESTATUS, 'estado'),
      getFromSrc(bySrc, SRC.ALERTA, 'estado'),
    ]),
    color: pickFirst([
      getFromSrc(bySrc, SRC.EDOMEX, 'color'),
      getFromSrc(bySrc, SRC.ESTATUS, 'color'),
      getFromSrc(bySrc, SRC.ALERTA, 'color'),
    ]),
    // Algunos source devuelven `motor`, otros `no_motor` (mismo concepto).
    motor: pickFirst([
      getFromSrc(bySrc, SRC.EDOMEX, 'motor'),
      getFromSrc(bySrc, SRC.ALERTA, 'motor'),
      getFromSrc(bySrc, SRC.ESTATUS, 'no_motor'),
    ]),
    tipo: getFromSrc(bySrc, SRC.EDOMEX, 'tipo'),
    // Datos del propietario — solo VEHICULOS EDOMEX los trae.
    propietario: getFromSrc(bySrc, SRC.EDOMEX, 'propietario'),
    rfc: getFromSrc(bySrc, SRC.EDOMEX, 'rfc'),
    domicilioPropietario: getFromSrc(bySrc, SRC.EDOMEX, 'domicilio'),
    // Estatus administrativo de la placa (00 = activa, etc.) + observación
    // libre del padrón (ej. "PLACAS ACTIVAS", "DADAS DE BAJA").
    estatusPlaca: getFromSrc(bySrc, SRC.EDOMEX, 'estatus_placa'),
    observaciones: getFromSrc(bySrc, SRC.EDOMEX, 'observaciones'),
    // Reserva — el legacy esperaba `tarjeta` pero ninguna de las fuentes
    // observadas hoy lo emite. Si el backend lo agrega más adelante,
    // probablemente vendrá en EDOMEX.
    tarjeta: getFromSrc(bySrc, SRC.EDOMEX, 'tarjeta'),
    // `reporte` consolidado: el campo que el banner pinta (ROJO/AZUL/VERDE).
    reporte: consolidateReporte(bySrc),
  }

  // ──────────────────────── roboData ────────────────────────
  // Solo se llena si hay datos de robo en alguna fuente. Mantenemos los
  // campos con su semántica original; el drawer decide qué mostrar y cómo.
  var hasEstatus = !!bySrc[SRC.ESTATUS]
  var hasAlerta = !!bySrc[SRC.ALERTA]
  var roboData = null
  if (hasEstatus || hasAlerta) {
    roboData = {
      // ESTATUS trae fecha del robo en ISO; ALERTA trae fecha del REPORTE
      // (cuándo se levantó la denuncia), con hora.
      fechaRobo: getFromSrc(bySrc, SRC.ESTATUS, 'fecha_robo'),
      fechaReporte: getFromSrc(bySrc, SRC.ALERTA, 'fecha_robo'),
      // Lugar del robo — entidad/municipio en ESTATUS, dirección textual
      // en ALERTA (el `domicilio` de ALERTA tiene label "DIRECCION ROBO").
      entidadRobo: getFromSrc(bySrc, SRC.ESTATUS, 'entidad_robo'),
      municipioRobo: getFromSrc(bySrc, SRC.ESTATUS, 'municipio_robo'),
      direccionRobo: getFromSrc(bySrc, SRC.ALERTA, 'domicilio'),
      // Dos averiguaciones distintas con el mismo `name` en backend, pero
      // semantica distinta — las preservamos por separado.
      averiguacion: getFromSrc(bySrc, SRC.ESTATUS, 'averiguacion'),
      averiguacionPrevia: getFromSrc(bySrc, SRC.ALERTA, 'averiguacion'),
      folioDenuncia: getFromSrc(bySrc, SRC.ESTATUS, 'folio_denuncia'),
      modalidad: getFromSrc(bySrc, SRC.ESTATUS, 'modalidad'),
      // Estatus oficial del reporte (CONFIRMADO / RECUPERADO) — solo lo
      // emite ALERTA. PREDENUNCIA viene en ESTATUS.reporte.
      estatusOficial: getFromSrc(bySrc, SRC.ALERTA, 'estatus'),
      // Texto crudo de cada fuente para ayudar a depurar inconsistencias.
      reporteEstatus: getFromSrc(bySrc, SRC.ESTATUS, 'reporte'),
      reporteAlerta: getFromSrc(bySrc, SRC.ALERTA, 'reporte'),
    }
    // Si terminó completamente vacío (todos los campos null) descártalo.
    var anyValue = false
    for (var k in roboData) {
      if (Object.prototype.hasOwnProperty.call(roboData, k) && roboData[k] != null) {
        anyValue = true
        break
      }
    }
    if (!anyValue) roboData = null
  }

  // ──────────────────────── placasAsociadas ────────────────────────
  // Filas adicionales NO fantasma con placa distinta = historial de placas
  // del mismo VIN. Se ignoran las filas fantasma (solo ALPR con serie
  // basura) — esas no son "otra placa", son la misma placa con datos
  // pobres del side-channel ALPR.
  var placasAsociadas = []
  var seen = {}
  if (vehiculoData.placa) seen[vehiculoData.placa] = true
  for (var i = 0; i < indexed.length; i++) {
    if (isPhantomRow(indexed[i])) continue
    var p = indexed[i].base.placa
    if (p && !seen[p] && !isGarbage(p)) {
      seen[p] = true
      placasAsociadas.push(p)
    }
  }

  return {
    vehiculoData: vehiculoData,
    roboData: roboData,
    placasAsociadas: placasAsociadas,
  }
}

// Resuelve el `reporte` que pinta el banner del drawer aplicando la
// jerarquía de confiabilidad observada en producción:
//   1. ALERTA.estatus = 'RECUPERADO' → 'RECUPERADO' (pista más fuerte;
//      ALERTA.reporte queda en 'SIN REPORTE' una vez recuperado)
//   2. ESTATUS.reporte = 'PREDENUNCIA' → 'PREDENUNCIA' (preliminar, banner
//      rojo pero distinguible del CONFIRMADO)
//   3. ESTATUS.reporte = 'RECUPERADO' → 'RECUPERADO'
//   4. ALERTA.reporte = 'CON REPORTE' (con ALERTA.estatus='CONFIRMADO')
//      → 'CON REPORTE'
//   5. cualquier otro caso con dato → ese mismo
//   6. nada → 'SIN REPORTE'
function consolidateReporte(bySrc) {
  var alertaEstatus = getFromSrc(bySrc, SRC.ALERTA, 'estatus')
  var alertaReporte = getFromSrc(bySrc, SRC.ALERTA, 'reporte')
  var estatusReporte = getFromSrc(bySrc, SRC.ESTATUS, 'reporte')
  var edomexReporte = getFromSrc(bySrc, SRC.EDOMEX, 'reporte')

  if (alertaEstatus === 'RECUPERADO' || estatusReporte === 'RECUPERADO' || edomexReporte === 'RECUPERADO') {
    return 'RECUPERADO'
  }
  if (estatusReporte === 'PREDENUNCIA') return 'PREDENUNCIA'
  if (alertaReporte === 'CON REPORTE') return 'CON REPORTE'
  if (estatusReporte && estatusReporte !== 'SIN REPORTE') return estatusReporte
  if (alertaReporte && alertaReporte !== 'SIN REPORTE') return alertaReporte
  if (edomexReporte && edomexReporte !== 'SIN REPORTE') return edomexReporte
  return 'SIN REPORTE'
}

// Recolecta TODAS las detecciones LPR del result, recorriendo todas las
// filas post-join. Antes hacíamos `result.queryObjects[0].extendedData`
// pero eso ignora ALPR cuando `Utility.join` lo separó en otra fila por
// tener baseProperties pobres (`serie="SIN INFORMACION"` en ALPR de
// placas robadas). Ahora extraemos de cualquier fila para no perderlas.
export function extractLprDataFromMurphyResult(queryObjects) {
  var rows = Array.isArray(queryObjects) ? queryObjects : []
  var lprData = []
  for (var i = 0; i < rows.length; i++) {
    var ext = rows[i] && rows[i].extendedData
    if (!Array.isArray(ext)) continue
    for (var j = 0; j < ext.length; j++) {
      if (ext[j] && ext[j].srcName === 'ALPR PLACA' && Array.isArray(ext[j].properties)) {
        lprData.push(ext[j].properties)
      }
    }
  }
  return lprData
}

// Categoría visual del banner. ROBO = rojo; RECUPERADO = azul; OK = verde.
// El drawer la usa para ramificar color/icono sin hardcodear valores.
export function categorizeReporte(reporte) {
  if (!reporte) return 'ok'
  var r = String(reporte).toUpperCase()
  if (r === 'RECUPERADO') return 'recuperado'
  if (r === 'SIN REPORTE') return 'ok'
  return 'robo'
}
